import { lazy, Suspense, useEffect, useState, type FormEvent } from "react";
import type { User } from "firebase/auth";
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Car,
  Camera,
  BrainCircuit,
  ReceiptText,
  Sparkles,
  UsersRound,
  WalletCards
} from "lucide-react";
import { trackEvent } from "./lib/analytics";
import {
  readWebsiteConsent,
  saveWebsiteConsent,
  subscribeToWebsiteConsent,
  type WebsiteConsent
} from "./lib/consent";

const PortalRoute = lazy(() => import("./routes/PortalRoute").then((module) => ({ default: module.PortalRoute })));

type Route = "home" | "portal" | "claim" | "privacy" | "terms";
type LeadStatus = "idle" | "sending" | "sent" | "error";

const appStoreUrl = import.meta.env.VITE_APP_STORE_URL ?? "";
const playStoreUrl = import.meta.env.VITE_PLAY_STORE_URL ?? "";
const leadCaptureEndpoint = import.meta.env.VITE_LEAD_CAPTURE_ENDPOINT ?? "";
const siteOrigin = import.meta.env.VITE_SITE_URL ?? "https://tolliq.co.uk";

const pageMeta: Record<Route, { title: string; description: string; path: string }> = {
  home: {
    title: "Tolliq | AI Charge Detection for Drivers and Fleets",
    description:
      "Tolliq uses AI to detect ANPR tolls, clean-air zones, parking and road charges, remind drivers before fines and support approved assisted payments.",
    path: "/"
  },
  portal: {
    title: "Tolliq Business Portal | Fleet Charge Visibility",
    description:
      "Secure Tolliq Business portal for driver invites, charge events, expenses, receipts, vehicles, payment status and reporting visibility.",
    path: "/portal"
  },
  claim: {
    title: "Claim a Tolliq Business Invite",
    description:
      "Connect your Tolliq driver app to a company workspace and approve business visibility for charge events, expenses, receipts and vehicles.",
    path: "/claim"
  },
  privacy: {
    title: "Privacy | Tolliq",
    description: "How Tolliq handles driver, vehicle, charge event, business portal and lead enquiry data.",
    path: "/privacy"
  },
  terms: {
    title: "Terms | Tolliq",
    description: "Terms for using the Tolliq website, access requests and business portal preview.",
    path: "/terms"
  }
};

const valueCards = [
  {
    icon: Camera,
    title: "ANPR cameras are everywhere",
    body: "Tolls, clean-air zones and car parks often log your number plate first and expect you to remember the payment later."
  },
  {
    icon: BrainCircuit,
    title: "AI detects likely charges",
    body: "Tolliq checks journey context, vehicle profile, operator rules and deadlines so you know what probably needs paying."
  },
  {
    icon: ReceiptText,
    title: "Pay once, prove it later",
    body: "Use assisted pay, keep receipts and stop digging through different operator portals after every journey."
  }
];

const portalMetrics = [
  { label: "Charge events", value: "1,284", detail: "business journeys detected" },
  { label: "Receipts matched", value: "96%", detail: "ready for expense review" },
  { label: "Outstanding", value: "£1,920", detail: "deadline and payment watch" },
  { label: "Fleet vehicles", value: "148", detail: "profiles linked to drivers" }
];

const driverStories = [
  {
    name: "Beckie Butcher",
    role: "Regular long-distance driver",
    quote:
      "I do long drives up to London and the payment websites are all different. Tolliq means I do not have to work out if I owe anything after the journey. If it needs paying, I can handle it in one flow."
  },
  {
    name: "Steven Ward",
    role: "Company car driver",
    quote:
      "This app would save me so much money in fines. I drive in new areas all the time and half the time I do not even realise I have gone through a payment zone."
  },
  {
    name: "Amelia Roberts",
    role: "Changes cars often",
    quote:
      "I change cars a lot, so being able to update my reg in one app is a big deal. I do not want to log in to loads of different platforms just to keep my details right."
  },
  {
    name: "Frank Regan",
    role: "Fleet operator",
    quote:
      "For the fleet, it is the receipts and payment status that matter. We can see what has been paid, what needs approval and stop chasing drivers after every airport or toll trip."
  }
];

function currentRoute(): Route {
  const path = window.location.pathname;
  if (path.startsWith("/portal")) return "portal";
  if (path.startsWith("/claim")) return "claim";
  if (path.startsWith("/privacy")) return "privacy";
  if (path.startsWith("/terms")) return "terms";
  return "home";
}

function navigate(path: string) {
  window.history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function setMeta(name: string, content: string) {
  const tag = document.querySelector(`meta[name="${name}"]`);
  tag?.setAttribute("content", content);
}

function setProperty(property: string, content: string) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function useRoute() {
  const [route, setRoute] = useState<Route>(currentRoute);

  useEffect(() => {
    const sync = () => setRoute(currentRoute());
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  return route;
}

export function App() {
  const route = useRoute();
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [consent, setConsent] = useState<WebsiteConsent | null>(() => readWebsiteConsent());
  const [showConsentPanel, setShowConsentPanel] = useState(() => !readWebsiteConsent());

  useEffect(() => {
    let unsubscribe: undefined | (() => void);
    let cancelled = false;

    Promise.all([import("firebase/auth"), import("./lib/firebase")])
      .then(([authModule, firebaseModule]) => {
        if (cancelled) return;
        unsubscribe = authModule.onAuthStateChanged(firebaseModule.getFirebaseAuth(), setAuthUser);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => subscribeToWebsiteConsent(setConsent), []);

  useEffect(() => {
    const meta = pageMeta[route];
    document.title = meta.title;
    setMeta("description", meta.description);
    setProperty("og:title", meta.title);
    setProperty("og:description", meta.description);
    setProperty("og:url", `${siteOrigin}${meta.path}`);
    setProperty("twitter:title", meta.title);
    setProperty("twitter:description", meta.description);
    setCanonical(`${siteOrigin}${meta.path}`);
    trackEvent("page_view", { route, path: meta.path, title: meta.title });
  }, [route]);

  return (
    <div className="site-shell">
      <Topbar route={route} isBusinessSignedIn={Boolean(authUser)} />
      {route === "portal" ? (
        <Suspense fallback={<main className="auth-page"><div className="loading-panel">Loading secure portal...</div></main>}>
          <PortalRoute />
        </Suspense>
      ) : null}
      {route === "claim" ? <ClaimRoute /> : null}
      {route === "privacy" ? <LegalRoute type="privacy" /> : null}
      {route === "terms" ? <LegalRoute type="terms" /> : null}
      {route === "home" ? <HomeRoute /> : null}
      <Footer onManageConsent={() => setShowConsentPanel(true)} />
      {showConsentPanel ? (
        <ConsentBanner
          initialConsent={consent}
          onClose={() => setShowConsentPanel(false)}
          onSave={(nextConsent) => {
            setConsent(nextConsent);
            setShowConsentPanel(false);
          }}
        />
      ) : null}
    </div>
  );
}

function Topbar({ route, isBusinessSignedIn }: { route: Route; isBusinessSignedIn: boolean }) {
  return (
    <header className="topbar">
      <button className="brand" type="button" onClick={() => navigate("/")} aria-label="Tolliq home">
        <span className="brand-mark">T</span>
        <span>Tolliq</span>
      </button>
      <nav className="nav-links" aria-label="Primary navigation">
        {route === "home" ? (
          <>
            <a href="#app">
              <span className="nav-full">Driver app</span>
              <span className="nav-short">App</span>
            </a>
            <a href="#mission">
              <span className="nav-full">Our mission</span>
              <span className="nav-short">Mission</span>
            </a>
            <a href="#intelligence">
              <span className="nav-full">How it works</span>
              <span className="nav-short">Works</span>
            </a>
            <a href="#business">
              <span className="nav-full">Fleet portal</span>
              <span className="nav-short">Fleets</span>
            </a>
            <a href="#get-started">
              <span className="nav-full">Request access</span>
              <span className="nav-short">Access</span>
            </a>
          </>
        ) : (
          <>
            <button type="button" onClick={() => navigate("/")}>
              Home
            </button>
            <button type="button" onClick={() => navigate("/claim")}>
              Claim invite
            </button>
          </>
        )}
      </nav>
      <button className="nav-cta" type="button" onClick={() => navigate("/portal")}>
        {isBusinessSignedIn ? "Business account" : "Business sign in"}
      </button>
    </header>
  );
}

function HomeRoute() {
  return (
    <main id="top">
      <section className="hero-section">
        <div className="hero-copy">
          <p className="eyebrow">AI charge detection for drivers</p>
          <h1>Tolliq</h1>
          <p className="hero-lede">
            Never get caught by a forgotten ANPR charge again. Tolliq spots likely tolls, clean-air zones and parking
            payments, warns you before fines and helps you complete approved assisted payments.
          </p>
        </div>

        <div className="product-scene" aria-label="Tolliq app preview">
          <div className="phone-visual">
            <div className="phone-status">
              <span>AI detected charge</span>
              <strong>94%</strong>
            </div>
            <div className="map-grid">
              <span className="map-surface surface-park" />
              <span className="map-surface surface-river" />
              <span className="map-zone zone-clean-air">Clean air zone</span>
              <span className="map-zone zone-toll">Toll point</span>
              <span className="map-road road-primary" />
              <span className="map-road road-secondary" />
              <span className="route route-a" />
              <span className="route route-b" />
              <span className="pin pin-a" />
              <span className="pin pin-b" />
              <span className="vehicle-marker" />
              <span className="map-label label-a">A40</span>
              <span className="map-label label-b">Ring Rd</span>
            </div>
            <div className="charge-card">
              <div>
                <span>London ULEZ</span>
                <strong>£12.50</strong>
              </div>
              <div className="charge-confidence">
                <BadgeCheck size={18} aria-hidden="true" />
                <span>Ready to pay</span>
              </div>
            </div>
            <div className="evidence-list">
              <span>We found the charge</span>
              <span>Your vehicle is covered</span>
              <span>Pay before the fine</span>
            </div>
          </div>
        </div>
        <div className="hero-proof">
          <span>14.4m private parking tickets were issued to UK drivers in 2024/25. Tolliq is built to make missed-charge fines disappear.</span>
          <small>RAC analysis of government DVLA data</small>
        </div>
        <div className="hero-conversion">
          <div className="hero-actions" id="downloads">
            <StoreButton kind="ios" />
            <StoreButton kind="android" />
            <button className="text-link" type="button" onClick={() => navigate("/portal")}>
              Business portal <ArrowRight size={17} aria-hidden="true" />
            </button>
          </div>
          <p className="launch-note">
            Store listings appear here as soon as they are live. Until then, request early access and we will send the
            official iOS or Android link for your region.
          </p>
        </div>
      </section>

      <section className="mission-band" id="mission">
        <div className="mission-copy">
          <p className="eyebrow">Our mission</p>
          <h2>Make missed-charge fines a thing drivers no longer have to fear.</h2>
          <p>
            A forgotten toll, airport drop-off or clean-air charge should not become a surprise letter, a penalty and
            another financial worry sitting in someone&apos;s head. Drivers are not missing payments because they do not
            care. They are being asked to remember too many deadlines across too many disconnected websites.
          </p>
          <p>
            Tolliq exists because this problem is getting bigger and no single service was built around the human
            reality of it. We bring the charge, deadline, payment and receipt into one place. The app is free to use; we
            only take a small commission when we help complete an approved assisted payment.
          </p>
        </div>
        <div className="mission-points" aria-label="Why Tolliq exists">
          {[
            ["Less anxiety", "Fewer surprise fines, fewer payment letters and less money stress after normal journeys."],
            ["One place to check", "Tolls, parking, clean-air zones and receipts should not live across separate portals."],
            ["Built for real life", "Drivers change cars, miss signs, cross new zones and forget deadlines. Tolliq is designed for that."],
            ["Free until we help", "Use Tolliq without a subscription. We earn only when an approved assisted payment is completed."]
          ].map(([title, body]) => (
            <article key={title}>
              <strong>{title}</strong>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="reviews-band" id="stories" aria-label="Example driver stories">
        <div className="reviews-heading">
          <p className="eyebrow">Driver stories</p>
          <h2>Built for the charge you only remember when the fine arrives.</h2>
          <p>
            Short example stories from the everyday moments Tolliq is designed to fix: airport parking, toll bridges,
            clean-air zones, fleet receipts and payment deadlines.
          </p>
        </div>
        <div className="reviews-grid">
          {driverStories.map((story) => (
            <article className="review-card" key={story.name}>
              <p>&ldquo;{story.quote}&rdquo;</p>
              <footer>
                <strong>{story.name}</strong>
                <span>{story.role}</span>
              </footer>
            </article>
          ))}
        </div>
      </section>

      <section className="lead-band" id="get-started">
        <div>
          <p className="eyebrow">Get started</p>
          <h2>Join the early access list before your next missed charge.</h2>
          <p>
            Join as a driver, fleet operator, finance lead or mobility partner. We will use your details only to respond
            to your Tolliq enquiry and share relevant product information.
          </p>
        </div>
        <LeadCaptureForm />
      </section>

      <section className="section compact" id="app">
        <div className="section-heading">
          <p className="eyebrow">For drivers</p>
          <h2>Your car is seen by cameras. Tolliq tells you what that means.</h2>
          <p>
            ANPR means Automatic Number Plate Recognition. It is used by toll roads, clean-air zones, car parks and
            enforcement networks to recognise your vehicle. The problem is simple: the camera knows you were there, but
            you still have to remember which operator to pay, by when, and where the receipt went.
          </p>
        </div>
        <div className="value-grid">
          {valueCards.map((card) => (
            <article className="value-card" key={card.title}>
              <card.icon size={26} aria-hidden="true" />
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section problem-band" id="problem">
        <div className="section-heading">
          <p className="eyebrow">The driver problem</p>
          <h2>One missed payment can turn a small charge into a stressful fine.</h2>
          <p>
            Drivers are expected to know every local toll, ULEZ-style zone, parking operator, payment portal and deadline.
            There is no single place to see what you owe, what you already paid and what still needs action.
          </p>
        </div>
        <div className="problem-grid">
          {[
            ["Camera first", "ANPR systems capture the vehicle automatically, often without a barrier or payment booth."],
            ["Memory later", "You may need to pay by midnight, the next day or within a short grace period on a separate website."],
            ["Fine if missed", "If you forget, the original charge can become a penalty, admin letter or debt collection problem."],
            ["No single tool", "Drivers and fleets have to manage tolls, parking, clean-air zones, receipts and expenses across fragmented operators."]
          ].map(([title, body]) => (
            <article key={title}>
              <strong>{title}</strong>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="split-section" id="intelligence">
        <div>
          <p className="eyebrow">How Tolliq works</p>
          <h2>AI charge detection before the penalty letter arrives.</h2>
          <p>
            Tolliq turns fragmented road charging into one simple flow: detect the likely charge, explain why, remind
            you before the deadline and guide approved assisted payments without sending you across disconnected portals.
          </p>
        </div>
        <div className="timeline-panel">
          {[
            ["Detect", "AI checks journey context against tolls, clean-air zones, parking and road-charge rules."],
            ["Explain", "Tolliq shows the charge, confidence, vehicle profile, deadline and evidence trail."],
            ["Resolve", "You approve, decline, or start assisted payment for matched charges."],
            ["Record", "Receipts, payment status and charge history stay in one place for expenses or fleet reporting."]
          ].map(([title, body], index) => (
            <div className="timeline-item" key={title}>
              <span>{index + 1}</span>
              <div>
                <strong>{title}</strong>
                <p>{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section business-band" id="business">
        <div className="section-heading">
          <p className="eyebrow">For companies and fleets</p>
          <h2>One view for fleet charges, payments, receipts and expenses.</h2>
          <p>
            Fleet teams face the same problem at scale: drivers miss operator deadlines, finance chases receipts and
            payments are spread across different portals. Tolliq centralises driver invites, payment methods, charge
            events, receipts, vehicles and reporting.
          </p>
        </div>
        <div className="business-grid">
          {[
            [UsersRound, "Driver directory", "Invite by app user ID or email and monitor pending, claimed, active and review states."],
            [WalletCards, "Expense control", "See paid, pending, failed and review-needed charges with receipt references."],
            [Car, "Vehicle visibility", "Tie charge activity to registrations, usage patterns and vehicle profiles."],
            [BarChart3, "Reporting", "Export finance-ready summaries for expenses, outstanding charges and audit checks."]
          ].map(([Icon, title, body]) => (
            <article className="business-card" key={title as string}>
              <Icon size={24} aria-hidden="true" />
              <h3>{title as string}</h3>
              <p>{body as string}</p>
            </article>
          ))}
        </div>
        <div className="business-dashboard-preview" aria-label="Example Tolliq business dashboard">
          <div className="dashboard-visual business-dashboard-card">
            <div className="dashboard-header">
              <span>Fleet overview</span>
              <strong>Business portal</strong>
            </div>
            {portalMetrics.map((metric) => (
              <div className="metric-row" key={metric.label}>
                <span>
                  {metric.label}
                  <small>{metric.detail}</small>
                </span>
                <strong>{metric.value}</strong>
              </div>
            ))}
          </div>
          <div className="business-dashboard-board">
            <div className="board-toolbar">
              <div>
                <span>Charge command centre</span>
                <strong>Payments, drivers and receipts in one place</strong>
              </div>
              <div className="board-actions" aria-label="Example finance actions">
                <button className="quiet-button" type="button">
                  Export receipts
                </button>
                <button className="report-button" type="button">
                  Review approved
                </button>
              </div>
            </div>
            <div className="board-metrics">
              <article>
                <span>Due this week</span>
                <strong>£842</strong>
              </article>
              <article>
                <span>Needs receipt</span>
                <strong>18</strong>
              </article>
              <article>
                <span>Assisted-pay ready</span>
                <strong>73%</strong>
              </article>
            </div>
            <div className="board-list">
              {[
                ["Maya Patel", "London ULEZ", "Approval needed", "£12.50", "review"],
                ["Owen Price", "Dart Charge", "Payment recorded", "£2.50", "complete"],
                ["Nina Shah", "Airport parking", "Approval needed", "£18.00", "review"]
              ].map(([driver, charge, status, amount, state]) => (
                <div className="board-row" key={`${driver}-${charge}`}>
                  <div>
                    <strong>{driver}</strong>
                    <span>{charge}</span>
                  </div>
                  <span className={`row-status ${state}`}>{status}</span>
                  <strong>{amount}</strong>
                  {state === "review" ? (
                    <div className="row-actions" aria-label={`Review ${driver} ${charge}`}>
                      <button type="button">Accept charge</button>
                      <button type="button">Decline</button>
                    </div>
                  ) : (
                    <div className="row-actions settled" aria-label={`${driver} ${charge} settled`}>
                      <span>Receipt saved</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="launch-cta">
          <div>
            <Sparkles size={22} aria-hidden="true" />
            <strong>Business onboarding ready</strong>
            <span>Firebase Auth protects the portal. Driver data is exposed only after invite claim and consent.</span>
          </div>
          <button className="nav-cta" type="button" onClick={() => navigate("/portal")}>
            Open portal
          </button>
        </div>
      </section>
    </main>
  );
}

function StoreButton({ kind }: { kind: "ios" | "android" }) {
  const url = kind === "ios" ? appStoreUrl : playStoreUrl;
  const isLive = Boolean(url);
  const label = kind === "ios" ? "App Store" : "Google Play";
  const small = isLive ? (kind === "ios" ? "Download on" : "Get it on") : "Launching soon";
  const Icon = kind === "ios" ? AppStoreIcon : PlayStoreIcon;

  if (isLive) {
    return (
      <a className={`store-button ${kind === "android" ? "secondary" : ""}`} href={url} target="_blank" rel="noreferrer">
        <Icon size={20} aria-hidden="true" />
        <span>
          <small>{small}</small>
          {label}
        </span>
      </a>
    );
  }

  return (
    <a className={`store-button pending ${kind === "android" ? "secondary" : ""}`} href="#get-started">
      <Icon size={20} aria-hidden="true" />
      <span>
        <small>{small}</small>
        {label}
      </span>
    </a>
  );
}

function AppStoreIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M16.55 12.71c-.02-2.24 1.83-3.34 1.91-3.39-1.05-1.53-2.67-1.74-3.23-1.76-1.36-.14-2.68.81-3.37.81-.7 0-1.76-.79-2.9-.77-1.49.02-2.88.89-3.64 2.25-1.57 2.72-.4 6.71 1.1 8.91.75 1.07 1.62 2.27 2.76 2.23 1.12-.05 1.54-.72 2.89-.72 1.34 0 1.73.72 2.9.69 1.2-.02 1.96-1.08 2.68-2.16.87-1.24 1.22-2.47 1.23-2.53-.03-.01-2.31-.88-2.33-3.56ZM14.34 6.11c.6-.75 1.01-1.76.9-2.78-.87.04-1.96.6-2.59 1.32-.56.64-1.06 1.69-.93 2.67.98.07 1.99-.49 2.62-1.21Z"
      />
    </svg>
  );
}

function PlayStoreIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="#34a853" d="M4.3 3.2c-.34.25-.55.67-.55 1.23v15.14c0 .56.21.98.55 1.23l8.46-8.79L4.3 3.2Z" />
      <path fill="#fbbc04" d="m15.48 9.19-2.72 2.82 2.72 2.82 3.27-1.86c1.03-.59 1.03-1.34 0-1.92l-3.27-1.86Z" />
      <path fill="#4285f4" d="m4.3 3.2 8.46 8.81 2.72-2.82L5.82 3.71c-.59-.34-1.12-.5-1.52-.51Z" />
      <path fill="#ea4335" d="m4.3 20.8c.4-.01.93-.17 1.52-.51l9.66-5.46-2.72-2.82L4.3 20.8Z" />
    </svg>
  );
}

function LeadCaptureForm() {
  const [status, setStatus] = useState<LeadStatus>("idle");
  const [message, setMessage] = useState("No spam. Just app availability, onboarding and product updates.");
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "Fleet or business",
    message: ""
  });

  async function submitLead(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.email || !form.name) {
      setStatus("error");
      setMessage("Add your name and email so we can respond.");
      return;
    }

    setStatus("sending");
    setMessage("Sending your access request...");
    trackEvent("lead_capture_started", { role: form.role, has_company: Boolean(form.company) });

    try {
      if (leadCaptureEndpoint) {
        const response = await fetch(leadCaptureEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...form, source: "tolliq-launch-site", submittedAt: new Date().toISOString() })
        });
        if (!response.ok) throw new Error("Lead capture endpoint rejected the request.");
      } else {
        window.localStorage.setItem("tolliq.launchLead.v1", JSON.stringify({ ...form, submittedAt: new Date().toISOString() }));
      }
      setStatus("sent");
      setMessage("Thanks. Your Tolliq access request is queued.");
      trackEvent("lead_capture_completed", {
        role: form.role,
        destination: leadCaptureEndpoint ? "endpoint" : "local_preview"
      });
    } catch {
      setStatus("error");
      setMessage("We could not send that request. Email hello@tolliq.co.uk or try again.");
      trackEvent("lead_capture_failed", { role: form.role });
    }
  }

  return (
    <form className="lead-form" onSubmit={submitLead}>
      <label>
        Name
        <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} autoComplete="name" />
      </label>
      <label>
        Work email
        <input
          value={form.email}
          onChange={(event) => setForm({ ...form, email: event.target.value })}
          type="email"
          autoComplete="email"
        />
      </label>
      <label>
        Company
        <input value={form.company} onChange={(event) => setForm({ ...form, company: event.target.value })} autoComplete="organization" />
      </label>
      <label>
        Interest
        <select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })}>
          <option>Fleet or business</option>
          <option>Driver app</option>
          <option>Finance or expenses</option>
          <option>Partner or operator</option>
        </select>
      </label>
      <label className="lead-message">
        What should we help with?
        <textarea value={form.message} onChange={(event) => setForm({ ...form, message: event.target.value })} rows={4} />
      </label>
      <button className="primary-button" type="submit" disabled={status === "sending"}>
        {status === "sending" ? "Sending..." : "Request access"}
      </button>
      <p className={`form-message ${status}`}>{message}</p>
    </form>
  );
}

function ClaimRoute() {
  const params = new URLSearchParams(window.location.search);
  const initialCode = params.get("code") ?? "";
  const [claimCode, setClaimCode] = useState(initialCode);
  const [email, setEmail] = useState("");
  const [appUserId, setAppUserId] = useState("");
  const [message, setMessage] = useState("Enter the claim code from your company invite.");

  function handleClaim() {
    if (!claimCode.trim() || (!email.trim() && !appUserId.trim())) {
      setMessage("Use the invite code plus your email or Tolliq app user ID.");
      return;
    }
    setMessage("Claim prepared. In production this posts to a verified Cloud Function before sharing business access.");
  }

  return (
    <main className="claim-page">
      <section className="claim-panel">
        <div>
          <p className="eyebrow">Driver invite claim</p>
          <h1>Connect your Tolliq app to your company.</h1>
          <p>
            Claiming an invite lets your company view agreed business charge events, expenses, receipts, vehicles,
            payment status and reports. Personal journeys remain outside business visibility unless you approve access.
          </p>
        </div>
        <div className="claim-form">
          <label>
            Invite code
            <input value={claimCode} onChange={(event) => setClaimCode(event.target.value)} placeholder="TLQ-..." />
          </label>
          <label>
            Tolliq app user ID
            <input value={appUserId} onChange={(event) => setAppUserId(event.target.value)} placeholder="usr_..." />
          </label>
          <label>
            Driver email
            <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" />
          </label>
          <div className="claim-steps">
            <span>1. Verify invite</span>
            <span>2. Confirm driver identity</span>
            <span>3. Approve business visibility</span>
          </div>
          <button className="primary-button" type="button" onClick={handleClaim}>
            Prepare claim
          </button>
          <p className="form-message">{message}</p>
        </div>
      </section>
    </main>
  );
}

function LegalRoute({ type }: { type: "privacy" | "terms" }) {
  const isPrivacy = type === "privacy";

  return (
    <main className="legal-page">
      <section className="legal-panel">
        <p className="eyebrow">{isPrivacy ? "Privacy notice" : "Website terms"}</p>
        <h1>{isPrivacy ? "Privacy" : "Terms of use"}</h1>
        <p className="legal-date">Last updated: 1 July 2026</p>
        {isPrivacy ? <PrivacyContent /> : <TermsContent />}
      </section>
    </main>
  );
}

function PrivacyContent() {
  return (
    <div className="legal-copy">
      <h2>Who we are</h2>
      <p>
        This website and the Tolliq business portal are provided by TOLLIQ LTD, a company registered in England and
        Wales with company number 17306039. Registered office: 66 Paul Street, London, England, United Kingdom, EC2A
        4NA. TOLLIQ LTD is the controller for personal data collected through this website unless a separate notice says
        otherwise.
      </p>
      <h2>What this notice covers</h2>
      <p>
        This notice covers tolliq.co.uk, the public website, access request forms, driver invite claim pages and the
        Tolliq Business portal. The mobile app has its own in-app Legal Centre covering app location, charge detection,
        assisted payment, receipts and account controls.
      </p>
      <h2>Personal data we collect</h2>
      <p>
        We may collect the details you submit to us, including name, email address, company, role, enquiry text, driver
        invite code, Tolliq app user ID and support messages. If you sign in to the business portal, we may process
        authentication identifiers, company workspace details, driver invite records, claim status, reporting settings,
        payment-status summaries, receipt metadata and portal activity needed to provide the service.
      </p>
      <h2>Driver, vehicle and charge data</h2>
      <p>
        Driver charge events, receipts, vehicles and payment status should be shared with a company only after a driver
        accepts an invite or otherwise grants business visibility. The public website does not expose private driver app
        data to unauthenticated visitors.
      </p>
      <h2>Why we use personal data</h2>
      <p>
        We use website and portal data to respond to enquiries, provide authenticated business access, manage driver
        invites, record consent and claim status, provide reporting, protect accounts, troubleshoot issues, measure
        website performance and improve the Tolliq service.
      </p>
      <h2>Lawful basis</h2>
      <p>
        Our lawful basis will usually be contract necessity where we provide portal access or respond to an access
        request, legitimate interests where we secure the website, prevent misuse and improve the service, consent where
        optional analytics or marketing communications require it, and legal obligation where we need to keep records for
        compliance, tax, accounting, disputes or regulatory reasons.
      </p>
      <h2>Cookies and analytics</h2>
      <p>
        The website uses essential browser storage for security, routing, authentication, portal sessions, access
        requests and remembering your privacy choices. These essential functions are needed for the website and portal to
        work properly.
      </p>
      <p>
        Optional analytics, including Firebase Analytics or similar tools, are used only if you choose analytics in the
        website consent banner. Analytics helps us understand aggregate website usage, page performance, product interest
        and which parts of the website help users. We do not use optional analytics until you have made that choice.
      </p>
      <p>
        Marketing and follow-up storage is off unless you choose it. If enabled in future, it may help us measure
        campaign performance or make relevant follow-up more useful. You can change or withdraw optional choices at any
        time using Cookie settings in the footer. You can also delete or block cookies and local storage through your
        browser settings, although this may affect sign-in, portal sessions or saved choices.
      </p>
      <h2>Website consent choices</h2>
      <p>
        We store your website consent choice locally in your browser so we know whether optional analytics and marketing
        storage are allowed. The consent record includes your selected categories, the time of the choice and the consent
        version. If we materially change how we use cookies, analytics or similar technologies, we may ask you to choose
        again.
      </p>
      <h2>Who we share data with</h2>
      <p>
        We may share data with service providers that help us run Tolliq, including Firebase/Google Cloud hosting,
        authentication, database, analytics, crash or messaging services, email providers, support tools, professional
        advisers and payment providers where payment services are enabled. We do not sell personal location data.
      </p>
      <h2>International transfers</h2>
      <p>
        Some providers may process data outside the UK. Where this happens, we expect appropriate safeguards to be used,
        such as adequacy regulations, standard contractual clauses or equivalent transfer mechanisms required by data
        protection law.
      </p>
      <h2>How long we keep data</h2>
      <p>
        We keep personal data only for as long as needed for the purpose it was collected, including providing the
        service, responding to enquiries, maintaining audit records, resolving disputes, meeting legal obligations and
        improving safety and reliability. Lead enquiries that do not become active customers should be reviewed and
        deleted or anonymised when no longer needed.
      </p>
      <h2>Your rights</h2>
      <p>
        Depending on your circumstances, you may have rights to access, correct, delete, restrict or object to processing
        of your personal data, request portability, withdraw consent and complain to the UK Information Commissioner’s
        Office. We may need to verify your identity before completing a request.
      </p>
      <h2>Account deletion and data export</h2>
      <p>
        Mobile app users can use in-app privacy controls to export local data, delete local data and request deletion of
        known cloud Tolliq account data. Website and business portal requests can be sent to the privacy contact below.
      </p>
      <h2>Children</h2>
      <p>
        Tolliq is designed for drivers, vehicle operators and business users. It is not aimed at children.
      </p>
      <h2>Changes to this notice</h2>
      <p>
        We may update this notice as Tolliq develops, including when payment providers, support tools, analytics settings
        or business portal features change. The latest version will be published on this page.
      </p>
      <h2>Contact</h2>
      <p>
        For privacy questions or rights requests, contact <a href="mailto:privacy@tolliq.co.uk">privacy@tolliq.co.uk</a>.
        For general support, contact <a href="mailto:support@tolliq.co.uk">support@tolliq.co.uk</a>.
      </p>
    </div>
  );
}

function TermsContent() {
  return (
    <div className="legal-copy">
      <h2>Who provides the website</h2>
      <p>
        This website is operated by TOLLIQ LTD, a company registered in England and Wales with company number 17306039.
        Registered office: 66 Paul Street, London, England, United Kingdom, EC2A 4NA.
      </p>
      <h2>Using this website</h2>
      <p>
        By using tolliq.co.uk, submitting an access request, claiming a driver invite or signing in to the business
        portal, you agree to use the website lawfully and in line with these terms. If you use the website on behalf of a
        company, you confirm that you have authority to do so.
      </p>
      <h2>What Tolliq does</h2>
      <p>
        Tolliq provides AI-assisted mobility charge intelligence, charge review, deadline reminders, receipt records,
        expense support, business reporting and assisted operator payment flows. Product capabilities, availability,
        supported operators and store links may change as the service develops.
      </p>
      <h2>No payment or legal advice</h2>
      <p>
        Tolliq helps organise charge intelligence and evidence. Drivers and businesses remain responsible for checking
        operator rules, official notices, payment deadlines, payment status, appeal routes and compliance obligations.
        Tolliq is not an official operator, authority, legal adviser or guaranteed charge detector.
      </p>
      <h2>Assisted payments</h2>
      <p>
        Where supported, Tolliq may help a user open an operator website, follow payment steps, record status and keep
        receipt evidence. Assisted payment does not mean unsupported operators are automatically paid. Any Tolliq fee,
        operator charge and total amount must be shown before a production payment is authorised.
      </p>
      <h2>Business portal access</h2>
      <p>
        Business users must use authorised accounts and invite drivers only where they have a legitimate basis to request
        business journey visibility. Driver consent and access controls must be respected. You must not add drivers,
        vehicles, payment methods or company details unless you have permission to do so.
      </p>
      <h2>Acceptable use</h2>
      <p>You must not use the website or portal to:</p>
      <ul>
        <li>break the law, misuse another person’s data or misrepresent your authority;</li>
        <li>attempt to bypass security, access another account or interfere with the service;</li>
        <li>submit false, misleading, abusive or harmful content;</li>
        <li>scrape, copy or reverse engineer the service except where allowed by law;</li>
        <li>use Tolliq to avoid lawful charges or interfere with operator systems.</li>
      </ul>
      <h2>Information accuracy</h2>
      <p>
        We try to keep website information accurate, but charge rules, operator websites, supported features, app store
        availability, pricing and integrations can change. You should not rely on website copy as the only source for an
        official charge, deadline, exemption or payment obligation.
      </p>
      <h2>Accounts and security</h2>
      <p>
        You are responsible for keeping sign-in details secure and for telling us promptly if you believe an account,
        invite code or business workspace has been used without authority. We may suspend access if we reasonably believe
        there is a security, legal, privacy or misuse risk.
      </p>
      <h2>Intellectual property</h2>
      <p>
        Tolliq’s name, logo, software, website design, copy, graphics, databases and product concepts are owned by or
        licensed to TOLLIQ LTD. You may use the website for normal service evaluation and portal use, but you may not copy
        or reuse it for a competing or unauthorised purpose.
      </p>
      <h2>Third-party services</h2>
      <p>
        Tolliq may link to app stores, operator websites, Firebase/Google services, payment providers, email providers,
        maps or other third-party services. Those services may have their own terms and privacy notices. Tolliq is not
        responsible for third-party websites that it does not control.
      </p>
      <h2>Cookie and analytics choices</h2>
      <p>
        Essential website storage is required for security, routing, sign-in, access requests and remembering consent
        choices. Optional analytics and marketing storage are controlled by the website consent banner and Cookie
        settings link in the footer. You can reject optional storage and still use the public website, although some
        portal or account features may need essential storage to work.
      </p>
      <h2>Availability</h2>
      <p>
        We aim to keep the website and portal available, but we do not guarantee uninterrupted access. Features may be
        changed, paused or withdrawn for maintenance, security, compliance, product improvement or provider availability.
      </p>
      <h2>Liability</h2>
      <p>
        Nothing in these terms excludes liability that cannot be excluded by law. To the extent permitted by law, Tolliq
        is not responsible for missed charges, fines, operator decisions, third-party outages, loss of profit, loss of
        business, indirect loss or consequences of relying on unverified charge information.
      </p>
      <h2>Governing law</h2>
      <p>
        These website terms are intended to be governed by the laws of England and Wales, unless mandatory consumer law
        gives you additional rights in another country.
      </p>
      <h2>Changes to these terms</h2>
      <p>
        We may update these terms as Tolliq develops. The latest version will be published on this page and will apply
        from the date shown above.
      </p>
      <h2>Contact</h2>
      <p>
        For terms or access questions, contact <a href="mailto:support@tolliq.co.uk">support@tolliq.co.uk</a>.
      </p>
    </div>
  );
}

function ConsentBanner({
  initialConsent,
  onClose,
  onSave
}: {
  initialConsent: WebsiteConsent | null;
  onClose: () => void;
  onSave: (consent: WebsiteConsent) => void;
}) {
  const [analytics, setAnalytics] = useState(Boolean(initialConsent?.analytics));
  const [marketing, setMarketing] = useState(Boolean(initialConsent?.marketing));
  const [view, setView] = useState<"summary" | "preferences">(initialConsent ? "preferences" : "summary");

  function persist(nextAnalytics: boolean, nextMarketing: boolean) {
    onSave(saveWebsiteConsent({ analytics: nextAnalytics, marketing: nextMarketing }));
  }

  if (view === "summary") {
    return (
      <div className="consent-backdrop summary" role="presentation">
        <section className="consent-summary-panel" role="dialog" aria-modal="true" aria-labelledby="cookie-summary-title">
          <div className="consent-copy">
            <p className="eyebrow">Website data choices</p>
            <h2 id="cookie-summary-title">Let’s tailor your Tolliq experience</h2>
            <p>
              We use essential storage to keep the site secure and remember your choices. With your permission, analytics
              helps us understand what people use so we can improve Tolliq. You can accept all, use essential only, or
              set your own preferences.
            </p>
            <button className="inline-link" type="button" onClick={() => navigate("/privacy")}>
              Privacy notice
            </button>
          </div>
          <div className="consent-summary-actions">
            <button className="secondary-button" type="button" onClick={() => setView("preferences")}>
              Set preferences
            </button>
            <button className="secondary-button" type="button" onClick={() => persist(false, false)}>
              Essential only
            </button>
            <button className="primary-button consent-primary" type="button" onClick={() => persist(true, true)}>
              Accept all
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="consent-backdrop preferences" role="presentation">
      <section className="consent-panel" role="dialog" aria-modal="true" aria-labelledby="cookie-consent-title">
        <header className="consent-header">
          <div className="brand compact-brand">
            <span className="brand-mark">T</span>
            <span>Tolliq</span>
          </div>
          <button
            className="consent-close"
            type="button"
            aria-label="Close cookie preferences"
            onClick={() => (initialConsent ? onClose() : setView("summary"))}
          >
            ×
          </button>
        </header>
        <div className="consent-preference-copy">
          <p className="eyebrow">Privacy preference centre</p>
          <h2 id="cookie-consent-title">Manage consent preferences</h2>
          <p>
            Choose which optional website data Tolliq can use. Essential storage is always active because it keeps the
            website secure, supports sign-in and remembers this choice.
          </p>
          <button className="inline-link" type="button" onClick={() => navigate("/privacy")}>
            More information
          </button>
        </div>
        <div className="consent-options preference-list">
          <ConsentOption
            title="Essential website storage"
            body="Required for security, portal sign-in, routing and remembering your privacy choices."
            checked
            disabled
          />
          <ConsentOption
            title="Analytics"
            body="Helps us measure anonymous website usage, page performance and product interest."
            checked={analytics}
            onChange={setAnalytics}
          />
          <ConsentOption
            title="Marketing and follow-up"
            body="Reserved for future campaign measurement or relevant follow-up. Off unless you choose it."
            checked={marketing}
            onChange={setMarketing}
          />
        </div>
        <div className="consent-actions">
          <button className="secondary-button" type="button" onClick={() => persist(false, false)}>
            Essential only
          </button>
          <button className="primary-button consent-primary" type="button" onClick={() => persist(analytics, marketing)}>
            {initialConsent ? "Update consent" : "Save choices"}
          </button>
          <button className="primary-button consent-primary" type="button" onClick={() => persist(true, true)}>
            Accept all
          </button>
        </div>
      </section>
    </div>
  );
}

function ConsentOption({
  title,
  body,
  checked,
  disabled = false,
  onChange
}: {
  title: string;
  body: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}) {
  return (
    <label className={`consent-option ${disabled ? "disabled" : ""}`}>
      <span className="consent-plus" aria-hidden="true">
        +
      </span>
      <span>
        <strong>{title}</strong>
        <small>{body}</small>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.checked)}
      />
    </label>
  );
}

function Footer({ onManageConsent }: { onManageConsent: () => void }) {
  return (
    <footer className="footer">
      <div>
        <strong>Tolliq</strong>
        <span>Confidence-led mobility charge intelligence.</span>
      </div>
      <div className="footer-links">
        <button type="button" onClick={() => navigate("/")}>
          Driver app
        </button>
        <button type="button" onClick={() => navigate("/portal")}>
          Fleet portal
        </button>
        <button type="button" onClick={() => navigate("/privacy")}>
          Privacy
        </button>
        <button type="button" onClick={() => navigate("/terms")}>
          Terms
        </button>
        <button type="button" onClick={onManageConsent}>
          Cookie settings
        </button>
        <a href="mailto:hello@tolliq.co.uk">Contact</a>
      </div>
    </footer>
  );
}
